<template>
  <el-dialog
    :visible.sync="dialogVisible"
    size="big"
    :append-to-body="true"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    :width="`${width}px`"
    :class="b()"
    @opened="handleOpen"
    @closed="handleClose"
  >
    <div :class="b('content')">
      <VideoPlayer
        ref="videoPlayerRef"
        v-bind="videoOptions"
        @play="handleVideoPlay"
        @ended="handleVideoPlayedEnd"
      />
      <em
        :class="['zkicon', 'iconguanbi', b('closed-icon')]"
        @click="handleCloseIconClick"
      />
      <VideoAutoCloseProgress
        v-if="showAutoCloseProgress"
        :class="b('progress')"
        :time="autoCloseDelay"
        @complete="handleAutoCloseProgressComplete"
      />
    </div>
  </el-dialog>
</template>

<script>
import { defineComponent, computed, ref, watch } from 'vue-demi';
import theme from '@zk/theme/lib/variables.scss';
import { videoProps, default as VideoPlayer } from './VideoPlayer';
import VideoAutoCloseProgress from './VideoAutoCloseProgress';
import { pick } from 'lodash';

export default defineComponent({
  name: 'VideoPlayerDialog',
  bemNS: theme.bemNS,
  components: { VideoPlayer, VideoAutoCloseProgress },
  props: {
    // 是否显示弹窗
    visible: {
      type: Boolean,
      default: false
    },
    // 播放完成是否自动关闭
    autoCloseAfterEnded: {
      type: Boolean,
      default: false
    },
    // 播放完成后自动关闭延迟时间（秒）
    autoCloseDelay: {
      type: Number,
      default: 5
    },
    width: {
      type: Number,
      default: 1080
    },
    // 打开弹窗触发
    onOpen: {
      type: Function,
      default: () => {}
    },
    // 关闭弹窗触发
    onClose: {
      type: Function,
      default: () => {}
    },
    ...videoProps
  },
  setup: (props) => {
    const dialogVisible = ref(false);
    const videoPlayerRef = ref(null);
    const showAutoCloseProgress = ref(false);

    watch(
      () => props.visible,
      (val) => {
        dialogVisible.value = val;
      },
      { immediate: true }
    );

    const videoOptions = computed(() => pick(props, Object.keys(videoProps)));

    const onKeyDown = (e) => {
      if (e.keyCode === 27) {
        closeDialog();
        e.stopPropagation();
      }
    };

    const handleOpen = async () => {
      props.onOpen();
      window.addEventListener('keydown', onKeyDown, true);
    };

    const handleClose = () => {
      const state = videoPlayerRef.value.getCurrentState();
      props.onClose(state);
      window.removeEventListener('keydown', onKeyDown, true);
    };

    const closeDialog = () => {
      dialogVisible.value = false;
    };

    const handleCloseIconClick = () => {
      closeDialog();
    };

    const handleVideoPlayedEnd = () => {
      if (props.autoCloseAfterEnded) {
        showAutoCloseProgress.value = true;
      }
    };

    const handleVideoPlay = () => {
      showAutoCloseProgress.value = false;
    };

    const handleAutoCloseProgressComplete = () => {
      showAutoCloseProgress.value = false;
      closeDialog();
    };

    return {
      videoPlayerRef,
      videoOptions,
      handleClose,
      handleCloseIconClick,
      handleOpen,
      handleVideoPlayedEnd,
      handleVideoPlay,
      dialogVisible,
      showAutoCloseProgress,
      handleAutoCloseProgressComplete
    };
  }
});
</script>

<style scoped lang="scss">
@include b(video-player-dialog) {
  ::v-deep {
    .el-dialog {
      border-radius: 16px;
      background: #000;
      height: 90%;
      margin: 50px;
      max-width: unset !important;
      min-width: unset !important;
      width: auto !important;
    }
    .el-dialog__header {
      display: none;
    }
    .el-dialog__body {
      padding: 0;
      height: 100%;
      overflow: hidden;
    }
  }
  @include e(content) {
    position: relative;
    width: 100%;
    height: 100%;
    padding: 50px;
    box-sizing: border-box;
    &:hover {
      @include e(closed-icon) {
        display: block;
      }
    }
  }
  @include e(closed-icon) {
    display: none;
    position: absolute;
    top: 10px;
    right: 10px;
    background: rgba(106, 106, 106, 0.5);
    padding: 6px;
    border-radius: 8px;
    font-size: 20px;
    color: #fff;
    cursor: pointer;
    &:hover {
      background: rgba(106, 106, 106, 0.8);
    }
  }
  @include e(progress) {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
